export const IndividualRfcLength = 13;
export const CompanyRfcLength = 12;

/**
 * Check if the RFC has a valid length:
 * - 12 characters for organizations
 * - 13 characters for individuals
 */
export function isValidRfcLength(rfc: string): boolean {
  return rfc.length === CompanyRfcLength || rfc.length === IndividualRfcLength;
}

/**
 * Check if the RFC follows the expected format:
 * For organizations (12 chars total):
 *   - First 3 letters (from the name)
 *   - Followed by 6-digit date (YYMMDD)
 *   - Followed by 3 alphanumeric characters
 *
 * For individuals (13 chars total):
 *   - First 4 letters (derived from paternal surname, maternal surname, and given name)
 *   - Followed by 6-digit date (YYMMDD)
 *   - Followed by 3 alphanumeric characters (including the checksum)
 *
 * The regex:
 *   - Captures the first 3 or 4 letters: ([A-ZÑ&]{3,4})
 *   - The date: (\d{6})
 *   - The next 2 characters: ([A-Z\d]{2})
 *   - An optional final character (which would be the checksum if 13 chars in total): ([A\d])?
 */
export function isValidRfcFormat(rfc: string): boolean {
  const rfcRegex = /^([A-ZÑ&]{3,4})(\d{6})([A-Z\d]{2})([A\d])?$/i;
  return rfcRegex.test(rfc);
}

/**
 * Checksum validation:
 * - Organization RFCs (12 chars) do not require checksum validation.
 * - Individual RFCs (13 chars) require checksum validation.
 */
export function isValidRfcChecksum(rfc: string): boolean {
  if (rfc.length === CompanyRfcLength) return true;

  if (rfc.length === IndividualRfcLength) {
    const rfcWithoutChecksum = rfc.slice(0, -1);
    const checksumChar = rfc.slice(-1).toUpperCase();

    const chars = '0123456789ABCDEFGHIJKLMN&OPQRSTUVWXYZ';
    const weights = [13, 12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2];

    let sum = 0;
    for (let i = 0; i < rfcWithoutChecksum.length; i++) {
      sum += chars.indexOf(rfcWithoutChecksum[i].toUpperCase()) * weights[i];
    }

    const modulo = sum % 11;
    const checksum =
      modulo === 0 ? '0' : modulo === 1 ? 'A' : (11 - modulo).toString();

    return checksum === checksumChar;
  }

  // Invalid length; cannot validate checksum
  return false;
}

/**
 * Complete RFC validation function that checks:
 * - Valid length (12 or 13 characters)
 * - Valid format (regex pattern)
 * - Valid checksum (for 13-character RFCs)
 */
export function isValidRfc(rfc: string): boolean {
  return (
    typeof rfc === 'string' &&
    isValidRfcLength(rfc) &&
    isValidRfcFormat(rfc) &&
    isValidRfcChecksum(rfc)
  );
}

/**
 * Formats the RFC value:
 * - Converts to uppercase.
 * - Removes any character that is not A-Z, 0-9, Ñ, or &.
 * - Limits the length to 13 characters.
 */
export function formatRfc(value: string): string {
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9Ñ&]/g, '')
    .slice(0, 13);
}

/**
 * Determines if an RFC is for an individual (13 chars) or company (12 chars)
 */
export function isIndividualRfc(rfc: string): boolean {
  return rfc.length === IndividualRfcLength;
}

/**
 * Determines if an RFC is for a company (12 chars) or individual (13 chars)
 */
export function isCompanyRfc(rfc: string): boolean {
  return rfc.length === CompanyRfcLength;
}

/**
 * Gets the RFC type description
 */
export function getRfcType(rfc: string): 'individual' | 'company' | 'unknown' {
  if (rfc.length === IndividualRfcLength) return 'individual';
  if (rfc.length === CompanyRfcLength) return 'company';
  return 'unknown';
}
